import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { Container, Typography, Grid, Card, CardHeader, CardContent, Box } from '@mui/material';
import Chart, { useChart } from '../components/chart';
import api from '../services/api';

// ----------------------------------------------------------------------

export default function AnalyticsPage() {
  const [typeData, setTypeData] = useState({ categories: [], series: [] });
  const [generationData, setGenerationData] = useState({ labels: [], series: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      
      // Carregar todos os Pokémon (1-905)
      const promises = [];
      for (let i = 1; i <= 905; i += 1) {
        promises.push(api.get(`/pokemon/${i}`));
      }
      
      const responses = await Promise.all(promises);
      const allPokemon = responses.map(res => res.data);
      
      // Processar dados por tipo
      const typeCount = {};
      allPokemon.forEach(pokemon => {
        pokemon.types.forEach(typeSlot => {
          const typeName = typeSlot.type.name;
          typeCount[typeName] = (typeCount[typeName] || 0) + 1;
        });
      });

      // Ordenar tipos por quantidade
      const sortedTypes = Object.entries(typeCount)
        .sort((a, b) => b[1] - a[1])
        .reduce((acc, [key, value]) => {
          acc.categories.push(key.charAt(0).toUpperCase() + key.slice(1));
          acc.series.push(value);
          return acc;
        }, { categories: [], series: [] });

      setTypeData(sortedTypes);

      // Processar dados por geração
      const generationRanges = [
        { name: 'Gen I', range: [1, 151] },
        { name: 'Gen II', range: [152, 251] },
        { name: 'Gen III', range: [252, 386] },
        { name: 'Gen IV', range: [387, 493] },
        { name: 'Gen V', range: [494, 649] },
        { name: 'Gen VI', range: [650, 721] },
        { name: 'Gen VII', range: [722, 809] },
        { name: 'Gen VIII', range: [810, 905] },
      ];

      const genCounts = generationRanges.map(gen => {
        const [start, end] = gen.range;
        return end - start + 1;
      });

      setGenerationData({
        labels: generationRanges.map(g => g.name),
        series: genCounts,
      });

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  // Cores dos tipos (mesmas do dashboard)
  const typeColors = {
    bug: '#92bc2c',
    dark: '#595761',
    dragon: '#0c69c8',
    electric: '#f2d94e',
    fairy: '#e91e63',
    fighting: '#d3425f',
    fire: '#fba54c',
    flying: '#a1bbec',
    ghost: '#5f6dbc',
    grass: '#5fbd58',
    ground: '#da7c4d',
    ice: '#75d0c1',
    normal: '#a0a29f',
    poison: '#b763cf',
    psychic: '#fa8581',
    rock: '#c9bb8a',
    steel: '#5695a3',
    water: '#539ddf',
  };

  // Mapear cores baseado nas categorias do gráfico
  const chartColors = typeData.categories.map(typeName => 
    typeColors[typeName.toLowerCase()] || '#999'
  );

  // Configuração do gráfico de barras (Tipos)
  const typeChartOptions = useChart({
    chart: {
      type: 'bar',
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 8,
        distributed: true,
      },
    },
    colors: chartColors,
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
      },
    },
    xaxis: {
      categories: typeData.categories,
      labels: {
        rotate: -45,
        style: {
          fontSize: '11px',
        },
      },
    },
    yaxis: {
      title: {
        text: 'Number of Pokémon',
      },
    },
    legend: {
      show: false,
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} Pokémon`,
      },
    },
  });

  // Configuração do gráfico de pizza (Gerações)
  const generationChartOptions = useChart({
    chart: {
      type: 'donut',
    },
    labels: generationData.labels,
    colors: ['#00AB55', '#1890FF', '#FFC107', '#FF4842', '#7635dc', '#00B8D9', '#FF6C40', '#FFE700'],
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '14px',
      markers: {
        radius: 12,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '16px',
              fontWeight: 600,
            },
            value: {
              show: true,
              fontSize: '24px',
              fontWeight: 'bold',
              formatter: (val) => val,
            },
            total: {
              show: true,
              label: 'Total',
              fontSize: '16px',
              fontWeight: 600,
              formatter: () => '905',
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => {
        const value = opts.w.config.series[opts.seriesIndex];
        return `${value}`;
      },
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
      },
      dropShadow: {
        enabled: false,
      },
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} Pokémon`,
      },
    },
  });

  return (
    <>
      <Helmet>
        <title>Analytics | Pokémon Dashboard</title>
      </Helmet>

      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 5 }}>
          📊 Pokémon Analytics
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h6" color="text.secondary">
              Loading analytics data...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This may take a few moments as we fetch data for 905 Pokémon
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Gráfico de Tipos */}
            <Grid item xs={12} lg={8}>
              <Card>
                <CardHeader
                  title="Pokémon Distribution by Type"
                  subheader="Number of Pokémon for each type (including dual types)"
                />
                <CardContent>
                  <Chart
                    type="bar"
                    series={[{ name: 'Pokémon', data: typeData.series }]}
                    options={typeChartOptions}
                    height={420}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Gráfico de Gerações */}
            <Grid item xs={12} lg={4}>
              <Card>
                <CardHeader
                  title="Distribution by Generation"
                  subheader="Total Pokémon per generation"
                />
                <CardContent>
                  <Chart
                    type="donut"
                    series={generationData.series}
                    options={generationChartOptions}
                    height={420}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Cards de Estatísticas Rápidas */}
            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.lighter' }}>
                <Typography variant="h3" color="primary.main">
                  905
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Pokémon
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'success.lighter' }}>
                <Typography variant="h3" color="success.main">
                  18
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Different Types
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.lighter' }}>
                <Typography variant="h3" color="warning.main">
                  8
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Generations
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'error.lighter' }}>
                <Typography variant="h3" color="error.main">
                  {typeData.categories[0] || '-'}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Most Common Type
                </Typography>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
}
